import { merge } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTrackAddedShareLinkExpirationDate, useTrackAddedShareLinkPasscode } from '@air/analytics';

import { DEFAULT_SHORT_URL_PROPS } from '~/components/SharePrivateCollectionModal/consts';
import { useUpdateCollectionMutation } from '~/components/SharePrivateCollectionModal/hooks/mutations/useUpdateCollectionMutation';
import {
  buildUpdateShortUrlParams,
  useUpdateShortUrlMutation,
} from '~/components/SharePrivateCollectionModal/hooks/mutations/useUpdateShortUrlMutation';
import { useGetCollection } from '~/components/SharePrivateCollectionModal/hooks/queries/useGetCollection';
import { useGetShortUrlForCollection } from '~/components/SharePrivateCollectionModal/hooks/queries/useGetShortUrlForCollection';
import {
  type CollectionProps,
  type PermissionMode,
  type ShortUrlProps,
} from '~/components/SharePrivateCollectionModal/types';
import { validateShortUrlProps } from '~/components/SharePrivateCollectionModal/utils/validation';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

export type UseEditSharePrivateCollectionProps = {
  collectionId: string;
};

export const useEditSharePrivateCollection = ({ collectionId }: UseEditSharePrivateCollectionProps) => {
  const { trackAddedShareLinkExpirationDate } = useTrackAddedShareLinkExpirationDate();
  const { trackAddedShareLinkPasscode } = useTrackAddedShareLinkPasscode();
  const { currentWorkspace } = useCurrentWorkspace();

  const { data: collectionData, isLoading: isLoadingCollection } = useGetCollection({ collectionId });
  const { data: shortUrlData, isLoading: isLoadingShortUrl } = useGetShortUrlForCollection({ collectionId });

  const isLoading = isLoadingCollection || isLoadingShortUrl;

  const collection = useMemo<CollectionProps>(
    () => ({
      id: collectionId,
      name: collectionData?.name ?? '',
      stats: collectionData?.stats,
    }),
    [collectionData, collectionId],
  );
  const shortUrl = useMemo<ShortUrlProps>(() => merge(DEFAULT_SHORT_URL_PROPS, shortUrlData), [shortUrlData]);

  const updateCollectionMutation = useUpdateCollectionMutation();
  const updateShortUrlMutation = useUpdateShortUrlMutation();

  // Local state ONLY for uncommitted changes
  const [uncommittedCollectionName, setUncommittedCollectionName] = useState<string | null>(null);
  const [uncommittedPasscode, setUncommittedPasscode] = useState<string | null>(null);
  const [uncommittedExpirationDate, setUncommittedExpirationDate] = useState<string | null>(null);

  // Validation runs on merged state (query data + uncommitted changes)
  const [validationErrors, setValidationErrors] = useState<Record<string, string> | null>(null);

  // Validate merged shortUrl state (server data + uncommitted changes) for real-time feedback
  useEffect(() => {
    if (!shortUrl) {
      return;
    }

    let isCancelled = false;

    const validate = async () => {
      const validation = await validateShortUrlProps({
        name: uncommittedCollectionName ?? collection.name,
        disabled: shortUrl.disabled,
        passcode: (uncommittedPasscode ?? shortUrl.passcode) || null,
        expirationDate: (uncommittedExpirationDate ?? shortUrl.expirationDate) || null,
        permissions: shortUrl.permissions,
      });

      if (!isCancelled) {
        setValidationErrors(validation.errors);
      }
    };

    validate();

    return () => {
      isCancelled = true;
    };
  }, [shortUrl, collection, uncommittedCollectionName, uncommittedPasscode, uncommittedExpirationDate]);

  // Update collection name in local uncommitted state only (doesn't save to backend)
  const updateCollectionName = useCallback((name: string) => {
    setUncommittedCollectionName(name);
  }, []);

  // Save collection name to backend (called by save button)
  const saveCollectionName = useCallback(async () => {
    const nameValue = uncommittedCollectionName;

    if (!nameValue) {
      return;
    }

    // Validate before saving
    const validation = await validateShortUrlProps({
      name: nameValue,
      disabled: shortUrl.disabled,
      passcode: shortUrl.passcode,
      expirationDate: shortUrl.expirationDate,
      permissions: shortUrl.permissions,
    });

    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    if (!currentWorkspace?.id) {
      throw new Error('No workspace id');
    }

    try {
      await updateCollectionMutation.mutateAsync({
        workspaceId: currentWorkspace?.id,
        collectionId,
        updates: { name: nameValue },
      });

      setUncommittedCollectionName(null);
      setValidationErrors(null);
    } catch {
      // Error is already handled by the mutation
    }
  }, [uncommittedCollectionName, collectionId, updateCollectionMutation, shortUrl, currentWorkspace?.id]);

  const updateLinkAccessPermission = useCallback(
    (value: boolean) => {
      if (!currentWorkspace?.id || !shortUrlData) {
        return;
      }
      updateShortUrlMutation.mutate(
        buildUpdateShortUrlParams(collectionId, currentWorkspace.id, shortUrlData, {
          disabled: !value,
        }),
      );
    },
    [collectionId, updateShortUrlMutation, currentWorkspace?.id, shortUrlData],
  );

  // Update asset permission - triggers immediate API call
  const updateAssetPermission = useCallback(
    <K extends keyof ShortUrlProps['permissions']['asset']>(
      key: K,
      value: ShortUrlProps['permissions']['asset'][K],
    ) => {
      if (!currentWorkspace?.id || !shortUrlData) {
        return;
      }
      updateShortUrlMutation.mutate(
        buildUpdateShortUrlParams(collectionId, currentWorkspace.id, shortUrlData, {
          permissions: {
            ...shortUrlData.permissions,
            asset: {
              ...shortUrlData.permissions.asset,
              [key]: value,
            },
          },
        }),
      );
    },
    [collectionId, updateShortUrlMutation, currentWorkspace?.id, shortUrlData],
  );

  // Update comment permissions - triggers immediate API call
  const updateCommentPermissions = useCallback(
    (mode: PermissionMode) => {
      const canViewDiscussions = mode === 'read-only' || mode === 'allow';
      const canManageDiscussions = mode === 'allow';

      if (!currentWorkspace?.id || !shortUrlData) {
        return;
      }
      updateShortUrlMutation.mutate(
        buildUpdateShortUrlParams(collectionId, currentWorkspace.id, shortUrlData, {
          permissions: {
            ...shortUrlData.permissions,
            asset: {
              ...shortUrlData.permissions.asset,
              canViewDiscussions,
              canManageDiscussions,
            },
          },
        }),
      );
    },
    [collectionId, updateShortUrlMutation, currentWorkspace?.id, shortUrlData],
  );

  // Update custom field permissions - triggers immediate API call
  const updateCustomFieldPermissions = useCallback(
    (mode: PermissionMode) => {
      const canView = mode === 'read-only' || mode === 'allow';
      const canManageApplications = mode === 'allow';

      if (!currentWorkspace?.id || !shortUrlData) {
        return;
      }
      updateShortUrlMutation.mutate(
        buildUpdateShortUrlParams(collectionId, currentWorkspace.id, shortUrlData, {
          permissions: {
            ...shortUrlData.permissions,
            customField: {
              ...shortUrlData.permissions.customField,
              canView,
              canManageApplications,
            },
          },
        }),
      );
    },
    [collectionId, updateShortUrlMutation, currentWorkspace?.id, shortUrlData],
  );

  // Update passcode in local uncommitted state only (doesn't save to backend)
  const updatePasscode = useCallback((passcode: string | null) => {
    setUncommittedPasscode(passcode);
  }, []);

  // Save passcode to backend (called by save button or when unchecking toggle)
  // Accepts optional value parameter to avoid race conditions with state updates
  const savePasscode = useCallback(
    async (value?: string | null) => {
      const passcodeValue = value !== undefined ? value : uncommittedPasscode;

      // Validate before saving (unless clearing)
      if (passcodeValue !== null) {
        const validation = await validateShortUrlProps({
          name: uncommittedCollectionName ?? collection.name,
          disabled: shortUrl.disabled,
          passcode: passcodeValue,
          expirationDate: shortUrl.expirationDate,
          permissions: shortUrl.permissions,
        });

        if (!validation.isValid) {
          setValidationErrors(validation.errors);
          return;
        }
      }

      if (!currentWorkspace?.id || !shortUrlData) {
        return;
      }

      try {
        await updateShortUrlMutation.mutateAsync(
          buildUpdateShortUrlParams(collectionId, currentWorkspace.id, shortUrlData, {
            passcode: passcodeValue,
          }),
        );

        setUncommittedPasscode(null);
        setValidationErrors(null);

        if (passcodeValue) {
          trackAddedShareLinkPasscode({ shortId: shortUrlData?.id ?? '' });
        }
      } catch {
        // Error is already handled by the mutation
      }
    },
    [
      uncommittedPasscode,
      uncommittedCollectionName,
      collectionId,
      updateShortUrlMutation,
      trackAddedShareLinkPasscode,
      shortUrl,
      collection,
      currentWorkspace?.id,
      shortUrlData,
    ],
  );

  // Update expiration date in local uncommitted state only (doesn't save to backend)
  const updateExpirationDate = useCallback((expirationDate: string | null) => {
    setUncommittedExpirationDate(expirationDate);
  }, []);

  // Save expiration date to backend (called by save button or when unchecking toggle)
  // Accepts optional value parameter to avoid race conditions with state updates
  const saveExpirationDate = useCallback(
    async (value?: string | null) => {
      const expirationDateValue = value !== undefined ? value : uncommittedExpirationDate;

      // Validate before saving (unless clearing)
      if (expirationDateValue !== null) {
        const validation = await validateShortUrlProps({
          name: uncommittedCollectionName ?? collection.name,
          disabled: shortUrl.disabled,
          passcode: shortUrl.passcode,
          expirationDate: expirationDateValue,
          permissions: shortUrl.permissions,
        });

        if (!validation.isValid) {
          setValidationErrors(validation.errors);
          return;
        }
      }

      if (!currentWorkspace?.id || !shortUrlData) {
        return;
      }

      try {
        await updateShortUrlMutation.mutateAsync(
          buildUpdateShortUrlParams(collectionId, currentWorkspace.id, shortUrlData, {
            expirationDate: expirationDateValue,
          }),
        );

        setUncommittedExpirationDate(null);
        setValidationErrors(null);

        if (expirationDateValue) {
          trackAddedShareLinkExpirationDate({ shortId: shortUrlData?.id ?? '' });
        }
      } catch {
        // Error is already handled by the mutation
      }
    },
    [
      uncommittedExpirationDate,
      uncommittedCollectionName,
      collectionId,
      updateShortUrlMutation,
      trackAddedShareLinkExpirationDate,
      shortUrl,
      collection,
      currentWorkspace?.id,
      shortUrlData,
    ],
  );

  return {
    collectionProps: collection,
    shortUrlProps: shortUrl,
    hasShortUrl: !!shortUrlData,
    isLoading,
    isSaving: updateCollectionMutation.isPending || updateShortUrlMutation.isPending,
    validationErrors,
    updateCollectionName,
    saveCollectionName,
    updateLinkAccessPermission,
    updateAssetPermission,
    updateCommentPermissions,
    updateCustomFieldPermissions,
    updatePasscode,
    updateExpirationDate,
    savePasscode,
    saveExpirationDate,
  };
};
